import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useRef } from 'react';
import type { MenuItemProps, MenuItemRef } from './MenuItem.data';
import styles from './MenuItem.module.scss';
import { setupMouseEnterTimeline, setupMouseLeaveTimeline } from './MenuItem.transitions';
import { useTransitionController } from 'util/react-transition-component';
import { renderCta } from '../../../util/cta/cta.template';
import Heading from '../heading/Heading';
import type { CtaRefType } from '../../../util/cta/cta.data';
import type { HeadingRef } from '../heading/Heading.data';

export default function MenuItem({
  ref,
  ...props
}: MenuItemProps & { ref?: Ref<MenuItemRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<CtaRefType | null>;
  const backgroundRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLHRElement>(null);
  const titleRef = useRef<HeadingRef>(null);
  const { children, className, disabled, ...ctaProps } = props;

  // useTransitionController(
  //   () => ({
  //     ref: elementRef,
  //     refs: {
  //       elementRef,
  //       lineRef,
  //       titleRef,
  //     },
  //     setupTransitionInTimeline,
  //     // setupTransitionOutTimeline,
  //   }),
  //   [],
  // );

  const hoverTransitionController = useTransitionController(
    () => ({
      refs: {
        backgroundRef,
      },
      setupTransitionInTimeline: setupMouseEnterTimeline,
      setupTransitionOutTimeline: setupMouseLeaveTimeline,
    }),
    [],
  );

  const onMouseEnter = useCallback(() => {
    hoverTransitionController.transitionIn();
  }, [hoverTransitionController]);

  const onMouseLeave = useCallback(() => {
    hoverTransitionController.transitionOut();
  }, [hoverTransitionController]);

  return renderCta({
    innerTemplate: (
      <>
        <hr ref={lineRef} />
        {typeof children === 'string' ? (
          <>
            <div ref={backgroundRef} className={classNames(styles.background, 'abs-fill')} />
            <Heading
              ref={titleRef}
              as="h5"
              size="heading-05"
              transitionInType="none"
              disableTriggerInOnScroll
              className={styles.label}
            >
              {children}
            </Heading>
          </>
        ) : (
          <div className={styles.childrenContainer}>{children}</div>
        )}
      </>
    ),
    props: {
      disabled,
      onMouseEnter,
      onMouseLeave,
      className: classNames(styles.menuItem, className, {
        [styles.disabled]: disabled,
      }),
      ...ctaProps,
    },
    elementRef,
  });
}
